import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as HeadLink, Meta, Title } from 'react-head'
import { useNavigate, Link } from 'react-router-dom'
import LazyImage from 'js/components/LazyImage/LazyImage'
import DefaultLayout from '../../components/DefaultLayout'
import Hero from '../../components/Hero/Hero'
import BalanceText from '../../components/BalanceText'
import { setSessionItem } from '../../storeManager'
import { languages } from '../../locale'
import PremiumButton from '../../components/PremiumButton/PremiumButton'
import Footer from '../../components/Footer/Footer'
import { FormattedMessage } from 'react-intl'
import messages from './messages'
import { selectIsPremium, selectIsLoggedIn } from '../../store/userSlice'
import Review from '../../components/Review'
import heart from 'media/heart-bubble.svg'
import heartDimensions from 'image-dimensions-loader!media/heart-bubble.svg'
import WhatIsLifesum from '../../components/WhatIsLifesum/WhatIsLifesum'
import Button from '../../components/Button/Button'
import quizApple from 'media/apple-quiz.svg'
import quizAppleDimensions from 'image-dimensions-loader!media/apple-quiz.svg'
import useBreakpoints from '../../hooks/useBreakpoints'
import Awards from '../../components/Awards'
import Animation from 'js/components/Animation/Animation'
import Fold from 'js/components/Fold'
import loadable from '@loadable/component'
import { useInView } from 'react-intersection-observer'
import { localizeLink } from '../../utils'
import useLocale from '../../hooks/useLocale'
import Highlights from 'js/pages/Home/Highlights'
import cn from 'classnames'
import { usePageCategory } from 'js/components/RouteChangeHandler'
import { getUTMParams } from 'js/UTMManager'
import { fetchPricing, setCoupon } from '../../store/pricingSlice'
import DiscountInfo from '../DatoNewAd/DatoNewAdDiscountInfo'
import AsSeenIn from '../../components/AsSeenIn/AsSeenIn'
import { chlorophyllSplashScreen } from 'js/featureFlags'
import ChlorophyllSplashScreen from './ChlorophyllSplashScreen'

const PlanTestOverlay = loadable(() =>
  import('js/components/PlanTest/PlanTestOverlay')
)

const Testimonials = loadable(() =>
  import('../../components/Testimonials/Testimonials')
)

function Home() {
  const [showPlanTest, setShowPlanTest] = useState(false)
  const [splashScreenDismissed, setSplashScreenDismissed] = useState(false)
  const isLoggedIn = useSelector((state) => selectIsLoggedIn(state))
  const isPremium = useSelector((state) => selectIsPremium(state))
  const navigate = useNavigate()
  const { LG, XXL } = useBreakpoints()
  const { language } = useLocale()
  const [testamonialsRef, testamonialsInView] = useInView({
    triggerOnce: true,
    threshold: 0.8,
  })
  const dispatch = useDispatch()
  const freeTrialCampaign = JSON.parse(getUTMParams()).utm_source === 'Regital'
  usePageCategory('Offer pages')

  useEffect(() => {
    if (localStorage.getItem('splashScreenDismissed')) {
      setSplashScreenDismissed(true)
    }
  }, [])

  useEffect(() => {
    setSessionItem('bannerShown', true)
  }, [])

  useEffect(() => {
    if (freeTrialCampaign) {
      dispatch(fetchPricing('7-days-2020')).then(() => {
        dispatch(setCoupon('7-days-2020'))
      })
    }
  }, [dispatch, freeTrialCampaign])

  const goToSignUpOrUpgrade = () => {
    if (isLoggedIn) {
      navigate('/upgrade')
    } else {
      navigate('/signup')
    }
  }

  // eslint-disable-next-line react/prop-types
  const ReadMore = ({ long = false, small = false }) => (
    <Link
      to={localizeLink('/features/', language)}
      className={cn(
        `
        c-button
        border-2 border-grey-darkest hover:border-green
        bg-transparent shadow-none hover:text-green
      `,
        small && 'c-button--small'
      )}
    >
      <span
        className={cn(
          !small && 'py-16 border-t-2 border-b-2 border-transparent'
        )}
      >
        <span className="relative" style={{ top: 1 }}>
          {long ? (
            <FormattedMessage
              id="Home.feedbackAndSupport.moreFeatures"
              defaultMessage="More Features"
            />
          ) : (
            <FormattedMessage
              id="features_page_readmore"
              defaultMessage="Read more"
            />
          )}
        </span>
      </span>
    </Link>
  )

  return (
    <div className="bg-Bg">
      <DefaultLayout>
        <Title>Lifesum Health App – Get Healthy & Lose Weight - Lifesum</Title>
        <Meta
          name="keywords"
          content="bmi, diet journal, weight loss, bmi calculator, healthy recipes, how to lose weight, health, nutrition facts, weight loss online, free calorie counter, lose weight, diet, motivation, fitness, workout, exercise, cardio, paleo, gluten free, vegan, vegetarian, iphone, android, iOS, samsung, barcode, calories, step counter, app, gain weight, diabetes, muscle, ketogenic"
        />
        <Meta
          name="description"
          content="Personalized diet plans, nutrition advice & healthy recipes. Lose weight the healthy way with nutritious food in the right portion sizes."
        />
        <HeadLink rel="canonical" href="https://lifesum.com/" />
        <HeadLink
          rel="alternate"
          href="https://lifesum.com/"
          hrefLang="x-default"
        />
        {languages.map((locale) => (
          <HeadLink
            rel="alternate"
            href={
              locale === 'en'
                ? `https://lifesum.com/`
                : `https://lifesum.com/${locale}/`
            }
            hrefLang={locale}
            key={locale}
          />
        ))}
        {chlorophyllSplashScreen && !splashScreenDismissed && (
          <ChlorophyllSplashScreen
            onDismiss={() => {
              localStorage.setItem('splashScreenDismissed', 'true')
              setSplashScreenDismissed(true)
            }}
          />
        )}
        {freeTrialCampaign && <DiscountInfo />}
        <Hero
          premium={isPremium}
          onClickFunc={goToSignUpOrUpgrade}
          scrollOffset={-110}
        />
        <div className="c-startpage text-Type">
          <div className="o-width-limiter">
            <div className="mt-36 mb-48 lg:hidden">
              <Awards />
            </div>
            <Highlights />
            <div className="my-56 md:my-72 lg:mt-96 lg:mb-56 max-w-1024 mx-auto">
              <AsSeenIn />
            </div>
            <h2
              className={`
              type-large-fat xl:type-56-bold
              text-center 
              lg:pt-56 xl:pt-64 
              mb-32 xl:mb-64
            `}
            >
              <FormattedMessage {...messages.what_is_lifesum} />
            </h2>
            <WhatIsLifesum />
            <div className="text-center mb-48 lg:mb-64">
              <ReadMore long small />
            </div>
            <Fold />
            <div className="bg-BgSub rounded-12 md:w-2/3 lg:w-21/24 xl:w-2/3 mx-auto lg:mt-24 xl:mt-64 mb-96 px-16 lg:px-64 pt-24 lg:pt-40 pb-40 xl:mb-128 text-center lg:text-left">
              <div className="lg:flex">
                <LazyImage
                  src={quizApple}
                  height={quizAppleDimensions.height}
                  width={quizAppleDimensions.width}
                  alt=""
                  className="self-center mb-16 lg:mb-0 relative lg:mr-64 flex-shrink-0"
                  style={{ width: XXL ? '240px' : LG ? '200px' : '170px' }}
                />
                <div className="self-center">
                  <h2 className="type-med-plus-fat mb-36 max-w-768 mx-auto">
                    <FormattedMessage {...messages.diet_quiz_heading} />
                  </h2>
                  <Button
                    big
                    style={{ minWidth: LG ? '240px' : '0' }}
                    onClick={() => {
                      setShowPlanTest(true)
                    }}
                  >
                    <FormattedMessage
                      {...messages['CTA.diet_test_header_button']}
                    />
                  </Button>
                  {showPlanTest && (
                    <PlanTestOverlay onClose={() => setShowPlanTest(false)} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-BgSub">
          <LazyImage
            className="mx-auto block mb-12 relative"
            src={heart}
            height={heartDimensions.height}
            width={heartDimensions.width}
            alt=""
            style={{
              bottom: 40,
              width: 130,
            }}
          />
          <div className="o-width-limiter">
            <div className="mx-auto max-w-1280 pb-56">
              <div className="hidden lg:block mb-56">
                <Awards />
              </div>
              <div className="md:flex">
                <div className="md:w-1/3 px-16">
                  <Review
                    text={
                      <FormattedMessage {...messages.appStoreReviewsText2} />
                    }
                  />
                </div>
                <div className="md:w-1/3 px-16">
                  <Review
                    text={
                      <FormattedMessage {...messages.appStoreReviewsText1} />
                    }
                  />
                </div>
                <div className="md:w-1/3 px-16">
                  <Review
                    text={
                      <FormattedMessage {...messages.appStoreReviewsText3} />
                    }
                  />
                </div>
              </div>
              <div className="max-w-768 mx-auto" ref={testamonialsRef}>
                {testamonialsInView && <Testimonials />}
              </div>
            </div>
          </div>
          {!isPremium && (
            <>
              <h2 className="type-large text-center px-16 mx-auto max-w-968  md:w-4/5">
                <BalanceText>
                  <FormattedMessage
                    {...messages.homepage_motivate_to_sign_up}
                    values={{
                      b: function BoldText(...chunks) {
                        return <b className="font-bold">{chunks}</b>
                      },
                    }}
                  />
                </BalanceText>
              </h2>

              <div className="lg:flex justify-center mt-40">
                <div className="hidden lg:block">
                  <ReadMore />
                </div>
                <div className="lg:mx-12">
                  <PremiumButton
                    onClickFunc={goToSignUpOrUpgrade}
                    newStartpage={true}
                    GALabel="Bottom desktop CTA"
                    marginless
                  />
                </div>
                <div className="text-center lg:mx-12 lg:hidden mt-32">
                  <ReadMore small />
                </div>
              </div>
            </>
          )}
          <Animation
            name="apple-lift"
            className="c-startpage__animation z-10 relative w-17/24 mt-40 md:w-half mx-auto -mb-72 md:-mb-80 lg:-mb-128 xxl:-mb-192"
            style={{ maxWidth: '400px' }}
          ></Animation>
        </div>
        <Footer marginless />
      </DefaultLayout>
    </div>
  )
}

export default Home
